import { mapKeys, kebabCase } from "lodash-es";
import type { KebabCase } from "type-fest";
import SvgIcon from "./svg-icons/SvgIcon.vue";

// import only required icons
import {
  AArrowUp,
  AArrowDown,
  ALargeSmall,
  ArrowBigRight,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowRightFromLine,
  ArrowRightToLine,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Baseline,
  Bold,
  BookMarked,
  Box,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronLast,
  ChevronsRight,
  ChevronsRightLeft,
  Circle,
  CircleAlert,
  CirclePlus,
  Clipboard,
  ClipboardPaste,
  ClipboardX,
  Cloud,
  Copy,
  CopyPlus,
  CreditCard,
  Download,
  Ellipsis,
  Equal,
  EqualNot,
  Eraser,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  FileText,
  Folder,
  FunctionSquare,
  GitCommitHorizontal,
  Grab,
  Group,
  Hand,
  Heading,
  Home,
  Image,
  Info,
  Italic,
  KeyRound,
  LaptopMinimal,
  Layers2,
  LayoutPanelTop,
  LayoutTemplate,
  LineChart,
  Link,
  Link2,
  List,
  ListChevronsDownUp,
  ListOrdered,
  Locate,
  Lock,
  LockOpen,
  Mail,
  Maximize,
  Maximize2,
  Menu,
  MessageCircle,
  Minimize,
  Minus,
  MoreHorizontal,
  MouseOff,
  MousePointer2,
  Network,
  Paintbrush,
  Palette,
  PartyPopper,
  Pause,
  Pencil,
  Pentagon,
  PenTool,
  Pin,
  PinOff,
  Plus,
  Pyramid,
  Quote,
  Redo,
  RefreshCcw,
  RefreshCw,
  Save,
  SaveOff,
  Search,
  Settings,
  Settings2,
  Share2,
  Sigma,
  Square,
  SquareArrowOutUpRight,
  SquarePlus,
  SquareRoundCorner,
  Star,
  Target,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignStart,
  Trash,
  Trash2,
  Triangle,
  TriangleAlert,
  Type,
  Underline,
  Undo,
  Unlink2,
  Upload,
  Users,
  Variable,
  View,
  X,
  ZoomIn,
  ZoomOut,
  Calendar,
  CalendarX,
  CalendarClock,
  ClipboardCheck,
  Grid2X2,
  Grid2X2Check,
  RotateCcw,
} from "@lucide/vue";

const iconComponents = {
  AArrowUp,
  AArrowDown,
  ALargeSmall,
  ArrowBigRight,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowRightFromLine,
  ArrowRightToLine,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Baseline,
  Bold,
  BookMarked,
  Box,
  Calendar,
  CalendarX,
  CalendarClock,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsRight,
  ChevronsRightLeft,
  Circle,
  CircleAlert,
  CirclePlus,
  Clipboard,
  ClipboardCheck,
  ClipboardPaste,
  ClipboardX,
  Cloud,
  CreditCard,
  Copy,
  CopyPlus,
  Download,
  Ellipsis,
  Equal,
  EqualNot,
  Eraser,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  FileText,
  Folder,
  FunctionSquare,
  GitCommitHorizontal,
  Grab,
  Grid2X2,
  Grid2X2Check,
  Group,
  Hand,
  Heading,
  Home,
  Image,
  Info,
  Italic,
  KeyRound,
  LaptopMinimal,
  Layers2,
  LayoutPanelTop,
  LayoutTemplate,
  LineChart,
  Link,
  Link2,
  List,
  ListChevronsDownUp,
  ListOrdered,
  Locate,
  Lock,
  LockOpen,
  Mail,
  Maximize,
  Maximize2,
  Menu,
  MessageCircle,
  Minimize,
  Minus,
  MoreHorizontal,
  MouseOff,
  MousePointer2,
  Network,
  Paintbrush,
  Palette,
  PartyPopper,
  Pause,
  Pin,
  PinOff,
  Pencil,
  Pentagon,
  PenTool,
  Plus,
  Pyramid,
  Quote,
  Redo,
  RefreshCcw,
  RefreshCw,
  RotateCcw,
  Save,
  SaveOff,
  Search,
  Settings,
  Settings2,
  Share2,
  Sigma,
  Square,
  SquareArrowOutUpRight,
  SquarePlus,
  SquareRoundCorner,
  Star,
  Target,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignStart,
  Trash,
  Trash2,
  Triangle,
  TriangleAlert,
  Type,
  Underline,
  Undo,
  Unlink2,
  Upload,
  Users,
  Variable,
  View,
  X,
  ZoomIn,
  ZoomOut,

  // custom icons:
  aArrowUpDown: SvgIcon,
  account: SvgIcon,
  action: SvgIcon,
  copySave: SvgIcon,
  doc: SvgIcon,
  h1: SvgIcon,
  h2: SvgIcon,
  h3: SvgIcon,
  hr: SvgIcon,
  impulse: SvgIcon,
  incident: SvgIcon,
  inverse: SvgIcon,
  layersPlus: SvgIcon,
  notInverse: SvgIcon,
  organization: SvgIcon,
  publicModels: SvgIcon,
  radarChart: SvgIcon,
  selectNet2: SvgIcon,
  selectNet: SvgIcon,
  settings: SvgIcon,
  sharedModels: SvgIcon,
  spiderChart: SvgIcon,
  subitem: SvgIcon,
  equalGreater: SvgIcon,
  equalLess: SvgIcon,
  roundedRectangle: SvgIcon,

  saveAs: SvgIcon,

  target: SvgIcon,
  resource: SvgIcon,
  index: SvgIcon,

  flow: ArrowBigRight,
  impact: ArrowRight
} as const;

type IconComponents = typeof iconComponents;

export const icons = mapKeys(iconComponents, (_i, key) => {
  return kebabCase(key);
}) as Omit<
  { [K in keyof IconComponents as KebabCase<K>]: IconComponents[K] },
  // we have to replace number-based entries manually as the
  // ts kebab-case seperates differently than the js separator
  "trash2" | "settings2" | "maximize2" | "bar-chart3" | "select-net2" | "grid2x2" | "grid2x2-check"
> & {
  "bar-chart-3": typeof BarChart3;
  "h-1": typeof iconComponents.h1;
  "h-2": typeof iconComponents.h2;
  "h-3": typeof iconComponents.h3;
  "layers-2": typeof Layers2;
  "trash-2": typeof Trash2;
  "settings-2": typeof Settings2;
  "share-2": typeof Share2;
  "maximize-2": typeof Maximize2;
  "select-net-2": typeof iconComponents.selectNet2;
  "mouse-pointer-2": typeof MousePointer2;
  "link-2": typeof Link2;
  "unlink-2": typeof Unlink2;
  "grid-2-x-2": typeof Grid2X2;
  "grid-2-x-2-check": typeof Grid2X2Check;
};

export type IconName = keyof typeof icons;
